import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Users } from 'lucide-react';

export function deepMatch(obj, term) {
  if (!obj || typeof obj !== 'object') return false;
  for (const k of Object.keys(obj)) {
    if (k === 'icon' || k === 'children') continue;
    if (k.toLowerCase().includes(term)) return true;
    const child = obj[k];
    if (child && typeof child === 'object') {
      if (deepMatch(child.children || child, term)) return true;
    }
  }
  return false;
}

export function countNodes(obj) {
  if (!obj || typeof obj !== 'object') return 0;
  let c = 0;
  for (const [k, v] of Object.entries(obj)) {
    if (k === 'icon' || k === 'children') continue;
    c += 1;
    if (v && typeof v === 'object') c += countNodes(v.children || v);
  }
  return c;
}

export function getChildEntries(node) {
  const children = node.children || node;
  if (typeof children !== 'object') return [];
  return Object.entries(children).filter(function(pair) { return pair[0] !== 'icon' && pair[0] !== 'children'; });
}

export function TreeNode(props) {
  var name = props.name;
  var node = props.node;
  var depth = props.depth;
  var searchTerm = props.searchTerm;
  var expertCounts = props.expertCounts;

  var _exp = useState(false);
  var expanded = _exp[0];
  var setExpanded = _exp[1];

  var entries = getChildEntries(node);
  var hasChildren = entries.length > 0;
  var termLower = searchTerm ? searchTerm.toLowerCase() : '';
  var nameMatches = termLower && name.toLowerCase().includes(termLower);

  useEffect(function() {
    if (!termLower) { setExpanded(depth < 1); return; }
    if (nameMatches || (hasChildren && deepMatch(node.children || node, termLower))) {
      setExpanded(true);
    }
  }, [termLower]);

  if (termLower) {
    var selfMatches = name.toLowerCase().includes(termLower);
    var childrenMatch = hasChildren && deepMatch(node.children || node, termLower);
    if (!selfMatches && !childrenMatch) return null;
  }

  var d = Math.min(depth, 4);
  var textClasses = [
    'text-white text-sm font-semibold',
    'text-zinc-200 text-sm font-medium',
    'text-zinc-300 text-[13px]',
    'text-zinc-400 text-xs',
    'text-zinc-400 text-xs'
  ];
  var expertCount = depth === 0 ? (expertCounts[name] || 0) : 0;

  var chevron;
  if (hasChildren) {
    chevron = expanded
      ? React.createElement(ChevronDown, { className: "w-3.5 h-3.5 text-zinc-500 mr-2 shrink-0" })
      : React.createElement(ChevronRight, { className: "w-3.5 h-3.5 text-zinc-600 mr-2 shrink-0" });
  } else {
    chevron = React.createElement('span', { className: "w-3.5 mr-2 shrink-0 flex justify-center" },
      React.createElement('span', { className: "w-1.5 h-1.5 rounded-full bg-zinc-700" })
    );
  }

  var childrenEl = null;
  if (expanded && hasChildren) {
    childrenEl = React.createElement('div', {
      className: "border-l " + (depth === 0 ? 'border-zinc-800/60' : 'border-zinc-800/40') + " ml-[9px]"
    }, entries.map(function(pair) {
      return React.createElement(TreeNode, {
        key: pair[0], name: pair[0], node: pair[1],
        depth: depth + 1, searchTerm: searchTerm, expertCounts: expertCounts
      });
    }));
  }

  var badge = expertCount > 0
    ? React.createElement('span', {
        className: "ml-auto text-[10px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full shrink-0"
      }, expertCount)
    : null;

  return React.createElement('div', {
    className: depth > 0 ? 'ml-4 sm:ml-6' : '',
    'data-testid': 'tree-node-' + name.replace(/[\s&,/]+/g, '-').toLowerCase()
  },
    React.createElement('button', {
      onClick: function() { if (hasChildren) setExpanded(!expanded); },
      className: "flex items-center w-full text-left py-1.5 px-2 rounded-lg group transition-all " +
        (nameMatches ? 'bg-amber-500/10 ring-1 ring-amber-500/30' : 'hover:bg-zinc-800/50')
    },
      chevron,
      React.createElement('span', { className: textClasses[d] + " truncate" }, name),
      hasChildren ? React.createElement('span', { className: "ml-2 text-[10px] text-zinc-600 shrink-0" }, entries.length) : null,
      badge
    ),
    childrenEl
  );
}
